import { useState } from 'react';
import { Loader2, RefreshCw, Sparkles, Wand2 } from 'lucide-react';
import client, { apiError } from '../adminApi';

// AI SEO Assistant — suggests an SEO title, meta description and content
// warnings for the record being edited. Advisory only: the editor reviews
// and applies each suggestion; nothing is auto-saved.
export const AiSeoPanel = ({ resource, doc, onApply }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const suggest = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await client.post('/admin/ai/seo-suggest', { resource, doc });
      setResult(res.data);
    } catch (err) {
      setError(apiError(err, 'AI suggestions are unavailable right now.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-seo-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> AI SEO Assistant
        </p>
        <button
          type="button"
          onClick={suggest}
          disabled={busy}
          data-testid="ai-seo-suggest"
          className="inline-flex items-center gap-1.5 rounded-md bg-navy-900 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-navy-700 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : result ? <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> : <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />}
          {busy ? 'Thinking…' : result ? 'Regenerate' : 'Suggest with AI'}
        </button>
      </div>

      {error && <p role="alert" data-testid="ai-seo-error" className="mt-3 text-xs font-semibold text-red-700">{error}</p>}

      {result && (
        <div className="mt-4 space-y-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Suggested title · {result.seoTitle.length} chars
            </p>
            <p className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm font-semibold text-navy-900" data-testid="ai-seo-title">
              {result.seoTitle}
            </p>
            <button
              type="button"
              onClick={() => onApply('title', result.seoTitle)}
              data-testid="ai-seo-apply-title"
              className="mt-1.5 rounded border border-slate-300 px-2.5 py-1 text-xs font-bold text-navy-900 hover:bg-navy-50"
            >
              Apply to SEO title
            </button>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Suggested description · {result.metaDescription.length} chars
            </p>
            <p className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm text-slate-700" data-testid="ai-seo-description">
              {result.metaDescription}
            </p>
            <button
              type="button"
              onClick={() => onApply('description', result.metaDescription)}
              data-testid="ai-seo-apply-description"
              className="mt-1.5 rounded border border-slate-300 px-2.5 py-1 text-xs font-bold text-navy-900 hover:bg-navy-50"
            >
              Apply to meta description
            </button>
          </div>
          {result.suggestions?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Content warnings</p>
              <ul className="mt-1.5 space-y-1.5" data-testid="ai-seo-warnings">
                {result.suggestions.map((s, i) => (
                  <li key={i} className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium leading-relaxed text-amber-900">
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            AI suggestions — review before applying
          </p>
        </div>
      )}
    </div>
  );
};
